import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { hardInvalidateInboxChannelCounts } from "@/lib/inbox-channel-counts";
import {
  INBOX_SCOPE_CHANGED_EVENT,
  inboxScopeChannelName,
  type InboxScopeChangedDetail,
} from "@/lib/inbox-scope-changed";

export function useInboxScopeInvalidation(
  workspaceId: string | null,
  onInvalidate: () => void,
  enabled = true,
) {
  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;

  useEffect(() => {
    if (!enabled || !workspaceId) return;

    const handle = (detail: InboxScopeChangedDetail) => {
      if (detail.workspaceId !== workspaceId) return;
      hardInvalidateInboxChannelCounts(detail.workspaceId);
      onInvalidateRef.current();
    };

    const onWindowEvent = (event: Event) => {
      const detail = (event as CustomEvent<InboxScopeChangedDetail>).detail;
      if (!detail) return;
      handle(detail);
    };

    window.addEventListener(INBOX_SCOPE_CHANGED_EVENT, onWindowEvent);

    const channel = supabase
      .channel(inboxScopeChannelName(workspaceId))
      .on("broadcast", { event: INBOX_SCOPE_CHANGED_EVENT }, ({ payload }) => {
        if (!payload || typeof payload !== "object") return;
        handle(payload as InboxScopeChangedDetail);
      })
      .subscribe();

    return () => {
      window.removeEventListener(INBOX_SCOPE_CHANGED_EVENT, onWindowEvent);
      supabase.removeChannel(channel);
    };
  }, [workspaceId, enabled]);
}
